import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Vehicle, VehicleType } from '@vehicle-logbook/shared';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Plus, Car, Edit, Trash2, Gauge, Eye } from 'lucide-react';
import { VehicleForm } from './VehicleForm';
import { VehicleDetails } from './VehicleDetails';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export const Vehicles = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  const { data: vehicles, isLoading } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      if (!user) return [];
      const token = await user.getIdToken();
      const response = await axios.get(`${API_URL}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data || [];
    },
    enabled: !!user,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      await axios.delete(`${API_URL}/vehicles/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success(t('vehicle.deleteSuccess'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('errors.generic'));
    },
  });

  const handleAdd = () => {
    setEditingVehicle(null);
    setIsFormOpen(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setIsFormOpen(true);
  };

  const handleView = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDetailsOpen(true);
  };

  const handleDelete = async (vehicle: Vehicle) => {
    if (window.confirm(t('vehicle.deleteConfirm'))) {
      await deleteMutation.mutateAsync(vehicle.id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingVehicle(null);
  };

  const handleDetailsClose = () => {
    setIsDetailsOpen(false);
    setSelectedVehicle(null);
  };

  const getVehicleTypeLabel = (type: VehicleType) => {
    return t(`vehicle.types.${type}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('vehicle.title')}
        </h1>
        <button
          onClick={handleAdd}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('vehicle.add')}
        </button>
      </div>

      {vehicles && vehicles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <Car className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">{t('vehicle.noVehicles')}</p>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('vehicle.add')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles?.map((vehicle: Vehicle) => (
            <div
              key={vehicle.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {vehicle.photoUrl ? (
                <img
                  src={vehicle.photoUrl}
                  alt={vehicle.name}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                  <Car className="w-16 h-16 text-gray-400" />
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {vehicle.name}
                </h3>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <p>
                    {vehicle.make} {vehicle.model} ({vehicle.year})
                  </p>
                  <p className="flex items-center">
                    <Gauge className="w-4 h-4 mr-2" />
                    {vehicle.currentMileage.toLocaleString()} {t('vehicle.km')}
                  </p>
                  {vehicle.licensePlate && (
                    <p>
                      {t('vehicle.licensePlate')}: {vehicle.licensePlate}
                    </p>
                  )}
                  <p className="text-primary-600">
                    {getVehicleTypeLabel(vehicle.type)}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleView(vehicle)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('common.view')}
                  </button>
                  <button
                    onClick={() => handleEdit(vehicle)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(vehicle)}
                    className="inline-flex items-center justify-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isFormOpen && (
        <VehicleForm
          vehicle={editingVehicle}
          onClose={handleFormClose}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['vehicles'] });
            handleFormClose();
          }}
        />
      )}

      {isDetailsOpen && selectedVehicle && (
        <VehicleDetails
          vehicle={selectedVehicle}
          onClose={handleDetailsClose}
          onEdit={() => {
            handleDetailsClose();
            handleEdit(selectedVehicle);
          }}
        />
      )}
    </div>
  );
};
